// created by AI
import type {
	FuelEvent,
	FuelEventsMetaData,
	FuelPageOpModeEventData,
	PlantWeatherSensorIds,
} from "api"
import type { OpModeIntervalEvent } from "./aquacultureTimeline"

export interface EventTableEntry {
	metadata: FuelEventsMetaData
	event: FuelEvent
}

export type FuelConsumptionTimelineTableItem = {
	id: number
	eventData: EventTableEntry[]
	date: Date
	dateEnd: Date
	duration: string
	opMode: string
	primaryOpMode: string
	fishMode: string
	fishPrimaryMode: string
	totalFuel: number
	deviation: { percent: number; fuel: number }
}

type OpMode = {
	id: number
	name: string
}

type BuildFuelTimelineRowsArgs = {
	canUserSeeBaseline: boolean
	fuelEventsMetaData: FuelPageOpModeEventData[] | undefined
	isFilteringSpecificOpMode: boolean
	listOfWeatherSensors: PlantWeatherSensorIds | undefined
	opModesList: OpMode[] | undefined
	rangeEnd: Date
	rangeStart: Date
	shouldSeparateLongEvents: boolean
	unrecognizedModeLabel: string
}

type BuildFuelTimelineRowsResult = {
	rawOpModeEvents: OpModeIntervalEvent[]
	rows: FuelConsumptionTimelineTableItem[]
}

const maxMergedEventDurationMs = 48 * 60 * 60 * 1000

type GroupableMainOpMode = "Port" | "Transit" | "DP"
type MergeStrategy = "exact-op-mode" | "main-op-mode"

function dateDiffInHoursAndMinutes(a: Date, b: Date): string {
	const msPerHour = 1000 * 60 * 60
	const aTimestamp = new Date(a).getTime()
	const bTimestamp = new Date(b).getTime()

	const hours = Math.floor((bTimestamp - aTimestamp) / msPerHour)
	const minutes = Math.floor(((bTimestamp - aTimestamp) % msPerHour) / (1000 * 60))
	return `${hours}h${minutes < 10 ? `0` : ``}${minutes}`
}

function scaleNullableValue(value: number | null, ratio: number): number | null {
	if (value === null) {
		return null
	}

	return value * ratio
}

function clipFuelEventToInterval(
	eventData: FuelEvent,
	rangeStart: Date,
	rangeEnd: Date
): FuelEvent | null {
	const eventStartMs = eventData.t1.getTime()
	const eventEndMs = eventData.t2.getTime()
	const clippedStartMs = Math.max(eventStartMs, rangeStart.getTime())
	const clippedEndMs = Math.min(eventEndMs, rangeEnd.getTime())
	const eventDurationMs = eventEndMs - eventStartMs
	const clippedDurationMs = clippedEndMs - clippedStartMs

	if (eventDurationMs <= 0 || clippedDurationMs <= 0) {
		return null
	}

	const visibleRatio = clippedDurationMs / eventDurationMs

	return {
		...eventData,
		t1: new Date(clippedStartMs),
		t2: new Date(clippedEndMs),
		fuelActualM3: eventData.fuelActualM3 * visibleRatio,
		fuelBaselineM3: scaleNullableValue(eventData.fuelBaselineM3, visibleRatio),
		plantTotalFuelM3: eventData.plantTotalFuelM3 * visibleRatio,
	}
}

function getWeatherSensorIds(listOfWeatherSensors: PlantWeatherSensorIds | undefined): number[] {
	if (listOfWeatherSensors === undefined) {
		return []
	}

	// Keep only concrete sensor ids; null values mean the plant has no mapped sensor for that
	// weather role and should not be passed into the expanded event queries.
	return Object.values(listOfWeatherSensors).filter((sensorId): sensorId is number => {
		return sensorId !== null
	})
}

function calculateDeviationFromEventEntries(
	eventData: EventTableEntry[],
	canUserSeeBaseline: boolean
): FuelConsumptionTimelineTableItem["deviation"] {
	if (canUserSeeBaseline !== true) {
		return {
			percent: 0,
			fuel: 0,
		}
	}

	const totals = eventData.reduce(
		(acc, entry) => {
			// Baseline-less slices cannot contribute to a baseline deviation. Keeping the actual
			// value out of both sums avoids overstating merged-row deviations.
			if (entry.event.fuelBaselineM3 === null || entry.event.fuelBaselineM3 === 0) {
				return acc
			}

			return {
				actualM3: acc.actualM3 + entry.event.fuelActualM3,
				baselineM3: acc.baselineM3 + entry.event.fuelBaselineM3,
			}
		},
		{
			actualM3: 0,
			baselineM3: 0,
		}
	)

	if (totals.actualM3 === 0 || totals.baselineM3 === 0) {
		return {
			percent: 0,
			fuel: 0,
		}
	}

	return {
		percent: Number(100 * (totals.actualM3 / totals.baselineM3 - 1)),
		fuel: totals.actualM3 - totals.baselineM3,
	}
}

function getDeviation(args: {
	canUserSeeBaseline: boolean
	eventData: FuelEvent
}): FuelConsumptionTimelineTableItem["deviation"] {
	if (args.canUserSeeBaseline !== true) {
		return {
			percent: 0,
			fuel: 0,
		}
	}

	const eventData = args.eventData
	const percent =
		eventData.fuelActualM3 === 0 ||
		eventData.fuelActualM3 === null ||
		eventData.fuelBaselineM3 === null
			? 0
			: Number(100 * (eventData.fuelActualM3 / eventData.fuelBaselineM3 - 1))

	const fuel =
		eventData.fuelActualM3 === null || eventData.fuelBaselineM3 === null
			? 0
			: eventData.fuelActualM3 - eventData.fuelBaselineM3

	return {
		percent,
		fuel,
	}
}

// created by AI
function getGroupableMainOpMode(opModeName: string): GroupableMainOpMode | undefined {
	const lowerCaseOpModeName = opModeName.toLowerCase()

	if (lowerCaseOpModeName.includes("port")) {
		return "Port"
	}
	if (lowerCaseOpModeName.includes("transit")) {
		return "Transit"
	}
	if (lowerCaseOpModeName.includes("dp")) {
		return "DP"
	}
	return undefined
}

function getMergeKey(
	row: FuelConsumptionTimelineTableItem,
	mergeStrategy: MergeStrategy
): string | undefined {
	if (mergeStrategy === "exact-op-mode") {
		const opModeId = row.eventData[0]?.metadata.opModeId
		return opModeId !== undefined ? `exact-op-mode:${opModeId}` : undefined
	}

	const mainOpMode = getGroupableMainOpMode(row.primaryOpMode)
	if (mainOpMode !== undefined) {
		return `main-op-mode:${mainOpMode}`
	}

	return undefined
}

function canMergeRows(
	previousRow: FuelConsumptionTimelineTableItem,
	currentRow: FuelConsumptionTimelineTableItem,
	mergeStrategy: MergeStrategy
): boolean {
	const previousMergeKey = getMergeKey(previousRow, mergeStrategy)
	const currentMergeKey = getMergeKey(currentRow, mergeStrategy)
	const previousEndMs = new Date(previousRow.dateEnd).getTime()
	const currentStartMs = new Date(currentRow.date).getTime()

	return (
		previousMergeKey !== undefined &&
		previousMergeKey === currentMergeKey &&
		previousEndMs === currentStartMs
	)
}

function mergeRows(
	previousRow: FuelConsumptionTimelineTableItem,
	currentRow: FuelConsumptionTimelineTableItem,
	canUserSeeBaseline: boolean,
	mergedOpModeName?: GroupableMainOpMode
): FuelConsumptionTimelineTableItem {
	const eventData = [...previousRow.eventData, ...currentRow.eventData]

	return {
		...previousRow,
		eventData,
		dateEnd: currentRow.dateEnd,
		duration: dateDiffInHoursAndMinutes(previousRow.date, currentRow.dateEnd),
		opMode: mergedOpModeName ?? previousRow.opMode,
		primaryOpMode: mergedOpModeName ?? previousRow.primaryOpMode,
		totalFuel: previousRow.totalFuel + currentRow.totalFuel,
		deviation: calculateDeviationFromEventEntries(eventData, canUserSeeBaseline),
	}
}

function mergeRowGroup(
	group: FuelConsumptionTimelineTableItem[],
	canUserSeeBaseline: boolean,
	mergeStrategy: MergeStrategy,
	shouldRelabelSeedRow: boolean
): FuelConsumptionTimelineTableItem {
	const firstRow = group[0]
	if (firstRow === undefined) {
		throw new Error("Cannot merge an empty operation mode event group")
	}

	const mergedMainOpMode =
		mergeStrategy === "main-op-mode"
			? getGroupableMainOpMode(firstRow.primaryOpMode)
			: undefined

	// Relabel the seed row too (not just rows folded in below via mergeRows), so a chunk of
	// exactly one raw slice — produced by splitting a longer multi-row run — still displays
	// under the shared main-group name instead of its own exact sub-mode (e.g.
	// "Port - Moored" -> "Port"). Gated by shouldRelabelSeedRow so a row that was never part
	// of a multi-row run to begin with keeps its more specific exact-mode name.
	const seedRow =
		shouldRelabelSeedRow && mergedMainOpMode !== undefined
			? { ...firstRow, opMode: mergedMainOpMode, primaryOpMode: mergedMainOpMode }
			: firstRow

	return group.slice(1).reduce((mergedRow, currentRow) => {
		return mergeRows(mergedRow, currentRow, canUserSeeBaseline, mergedMainOpMode)
	}, seedRow)
}

const targetChunkDurationMs = 24 * 60 * 60 * 1000

function groupAdjacentRowsByOpMode(
	rows: FuelConsumptionTimelineTableItem[],
	mergeStrategy: MergeStrategy
): FuelConsumptionTimelineTableItem[][] {
	const groups: FuelConsumptionTimelineTableItem[][] = []

	for (const currentRow of rows) {
		const currentGroup = groups.at(-1)
		const previousRow = currentGroup?.at(-1)

		if (
			currentGroup !== undefined &&
			previousRow !== undefined &&
			canMergeRows(previousRow, currentRow, mergeStrategy)
		) {
			currentGroup.push(currentRow)
			continue
		}

		groups.push([currentRow])
	}

	return groups
}

// created by AI
function mergeAdjacentRowsByStrategy(
	rows: FuelConsumptionTimelineTableItem[],
	canUserSeeBaseline: boolean,
	mergeStrategy: MergeStrategy
): FuelConsumptionTimelineTableItem[] {
	return groupAdjacentRowsByOpMode(rows, mergeStrategy).map((group) => {
		// Only relabel a lone raw slice to the shared main-group name if it's actually part of
		// a run that got grouped with at least one other row — a row that was never adjacent to
		// another same-main-group row keeps its more specific exact-mode name.
		const shouldRelabelSeedRow = group.length > 1
		return mergeRowGroup(group, canUserSeeBaseline, mergeStrategy, shouldRelabelSeedRow)
	})
}

function mergeAdjacentRows(
	rows: FuelConsumptionTimelineTableItem[],
	canUserSeeBaseline: boolean,
	isFilteringSpecificOpMode: boolean
): FuelConsumptionTimelineTableItem[] {
	const chronologicalRows = [...rows].sort((a, b) => {
		return new Date(a.date).getTime() - new Date(b.date).getTime()
	})

	const exactOpModeRows = mergeAdjacentRowsByStrategy(
		chronologicalRows,
		canUserSeeBaseline,
		"exact-op-mode"
	)

	if (isFilteringSpecificOpMode === true) {
		return exactOpModeRows
	}

	return mergeAdjacentRowsByStrategy(exactOpModeRows, canUserSeeBaseline, "main-op-mode")
}

// The backend already splits raw events into day-sized slices anchored to the query's
// `rangeStart` (a synthetic same-op-mode boundary is inserted every 24h from there, even
// when the op mode doesn't change), so a real event edge always exists at
// `rangeStart + n * 24h`. Cutting at those same absolute instants — rather than re-deriving
// split points from accumulated row durations — means merging across op modes, or across an
// uneven leading slice, never drifts the cut away from the real day edge the data was
// already aligned to.
function findDayBoundaryTimestampsMs(
	groupStartMs: number,
	groupEndMs: number,
	rangeStartMs: number
): number[] {
	const firstDayIndex = Math.ceil((groupStartMs - rangeStartMs) / targetChunkDurationMs)
	const boundaryTimestampsMs: number[] = []

	for (let dayIndex = firstDayIndex; ; dayIndex++) {
		const boundaryMs = rangeStartMs + dayIndex * targetChunkDurationMs
		if (boundaryMs >= groupEndMs) {
			break
		}
		if (boundaryMs > groupStartMs) {
			boundaryTimestampsMs.push(boundaryMs)
		}
	}

	return boundaryTimestampsMs
}

function buildRowFromEventEntries(
	eventData: EventTableEntry[],
	template: FuelConsumptionTimelineTableItem,
	canUserSeeBaseline: boolean
): FuelConsumptionTimelineTableItem {
	const firstEvent = eventData[0]!.event
	const lastEvent = eventData.at(-1)!.event
	const totalFuel = eventData.reduce((sum, entry) => sum + entry.event.plantTotalFuelM3, 0)

	return {
		...template,
		eventData,
		date: firstEvent.t1,
		dateEnd: lastEvent.t2,
		duration: dateDiffInHoursAndMinutes(firstEvent.t1, lastEvent.t2),
		totalFuel,
		deviation: calculateDeviationFromEventEntries(eventData, canUserSeeBaseline),
	}
}

// A row's `eventData` retains every raw sub-event that went into it, regardless of how many
// merge passes/op modes contributed — so instead of trying to cut between already-merged
// rows (which can hide a day boundary inside a single merged row, e.g. when two different
// exact op modes both happen to be active right up to and right after midnight), split a
// too-long row directly at its own day-boundary-crossing raw sub-events.
function explodeRowAtDayBoundaries(
	row: FuelConsumptionTimelineTableItem,
	rangeStart: Date,
	canUserSeeBaseline: boolean
): FuelConsumptionTimelineTableItem[] {
	const groupStartMs = row.date.getTime()
	const groupEndMs = row.dateEnd.getTime()

	if (groupEndMs - groupStartMs <= maxMergedEventDurationMs || row.eventData.length < 2) {
		return [row]
	}

	const boundaryTimestampsMs = findDayBoundaryTimestampsMs(
		groupStartMs,
		groupEndMs,
		rangeStart.getTime()
	)

	if (boundaryTimestampsMs.length === 0) {
		return [row]
	}

	const eventDataChunks: EventTableEntry[][] = []
	let sliceStart = 0
	let boundaryIndex = 0

	for (
		let entryIndex = 1;
		entryIndex < row.eventData.length && boundaryIndex < boundaryTimestampsMs.length;
		entryIndex++
	) {
		if (row.eventData[entryIndex]!.event.t1.getTime() >= boundaryTimestampsMs[boundaryIndex]!) {
			eventDataChunks.push(row.eventData.slice(sliceStart, entryIndex))
			sliceStart = entryIndex
			boundaryIndex++
		}
	}

	eventDataChunks.push(row.eventData.slice(sliceStart))

	return eventDataChunks.map((eventData) =>
		buildRowFromEventEntries(eventData, row, canUserSeeBaseline)
	)
}

export function buildFuelTimelineRowsFromEndpointEvents(
	args: BuildFuelTimelineRowsArgs
): BuildFuelTimelineRowsResult {
	const rows: FuelConsumptionTimelineTableItem[] = []
	const rawOpModeEvents: OpModeIntervalEvent[] = []
	const weatherSensorsId = getWeatherSensorIds(args.listOfWeatherSensors)

	let rowId = 0
	for (const evMetadata of args.fuelEventsMetaData ?? []) {
		const currentModeName =
			args.opModesList?.find((opMode) => opMode.id === evMetadata.metaData.opModeId)?.name ??
			`(${args.unrecognizedModeLabel})`

		for (const eventData of evMetadata.events) {
			const visibleEventData =
				args.shouldSeparateLongEvents === true
					? clipFuelEventToInterval(eventData, args.rangeStart, args.rangeEnd)
					: eventData
			if (visibleEventData === null) {
				continue
			}

			const eventDataComplete: EventTableEntry = {
				metadata: {
					...evMetadata.metaData,
					weatherSensorsId,
				},
				event: visibleEventData,
			}

			// Keep the op-mode slice aligned with the visible interval so rows that ended before
			// the selected range disappear and boundary rows start at the selected range.
			rawOpModeEvents.push({
				opMode: currentModeName,
				start: visibleEventData.t1,
				end: visibleEventData.t2,
				eventData: eventDataComplete,
			})

			rows.push({
				id: rowId,
				eventData: [eventDataComplete],
				date: visibleEventData.t1,
				dateEnd: visibleEventData.t2,
				totalFuel: visibleEventData.plantTotalFuelM3,
				duration: dateDiffInHoursAndMinutes(visibleEventData.t1, visibleEventData.t2),
				opMode: currentModeName,
				primaryOpMode: currentModeName,
				fishMode: "--",
				fishPrimaryMode: "--",
				deviation: getDeviation({
					canUserSeeBaseline: args.canUserSeeBaseline,
					eventData: visibleEventData,
				}),
			})
			rowId = rowId + 1
		}
	}

	const mergedRows = mergeAdjacentRows(
		rows,
		args.canUserSeeBaseline,
		args.isFilteringSpecificOpMode
	)

	const explodedRows =
		args.shouldSeparateLongEvents === true
			? mergedRows.flatMap((row) =>
					explodeRowAtDayBoundaries(row, args.rangeStart, args.canUserSeeBaseline)
				)
			: mergedRows

	const finalRows = explodedRows.map((row, index) => ({ ...row, id: index }))

	finalRows.sort((a, b) => {
		return new Date(b.date).getTime() - new Date(a.date).getTime()
	})

	return {
		rawOpModeEvents,
		rows: finalRows,
	}
}
